import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

type Variant = 'default' | 'destructive' | 'outline' | 'ghost' | 'link';
type Size = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  loading?: boolean;
  htmlType?: AntButtonProps['htmlType'];
  type?: AntButtonProps['htmlType'];
  icon?: ReactNode;
}

function mapType(variant: Variant): AntButtonProps['type'] {
  switch (variant) {
    case 'default':
      return 'primary';
    case 'destructive':
      return 'primary';
    case 'outline':
      return 'default';
    case 'ghost':
      return 'text';
    case 'link':
      return 'link';
  }
}

function mapSize(size: Size): AntButtonProps['size'] {
  switch (size) {
    case 'sm':
      return 'small';
    case 'lg':
      return 'large';
    case 'icon':
      return 'middle';
    default:
      return 'middle';
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'default',
      size = 'md',
      asChild: _asChild,
      loading,
      icon,
      children,
      disabled,
      type,
      htmlType,
      className,
      ...rest
    },
    ref,
  ) => {
    const danger = variant === 'destructive';
    return (
      <AntButton
        ref={ref as never}
        type={mapType(variant)}
        size={mapSize(size)}
        danger={danger}
        loading={loading}
        disabled={disabled}
        icon={icon}
        shape={size === 'icon' ? 'circle' : 'default'}
        htmlType={htmlType ?? (type as AntButtonProps['htmlType']) ?? 'button'}
        className={className}
        {...rest}
      >
        {children}
      </AntButton>
    );
  },
);
Button.displayName = 'Button';
